import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { ProgressBar } from './tracking';

const TYPE_LABEL = { book: 'Diaporama', audio: 'Audio', video: 'Vidéo', document: 'Document' };
const fmt = (v) => (v ? new Date(v).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const fmtPos = (row, type) => {
  if (type === 'book') return `slide ${Math.round(row.max_position || 0)} / ${row.duration || '?'}`;
  const m = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, '0')}`;
  return `${m(row.max_position)} / ${m(row.duration)}`;
};

export function SuiviDialog({ open, onClose }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState('');
  useEffect(() => {
    if (!open) return;
    api().get('/formation/suivi').then(({ data: d }) => { setData(d); setSel(s => s || d.items.find(i => i.type !== 'document' || i.quiz_count > 0)?.id || ''); })
      .catch(() => toast.error('Suivi indisponible'));
  }, [open, api]);
  const item = useMemo(() => data?.items.find(i => i.id === sel), [data, sel]);
  const notSeen = useMemo(() => {
    if (!item || !data) return [];
    const seen = new Set(item.viewers.map(v => v.user_id));
    return data.users.filter(u => !seen.has(u.id));
  }, [item, data]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface sm:w-full max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="suivi-formation-dialog">
        <DialogHeader><DialogTitle className="text-ink">Suivi des formations</DialogTitle></DialogHeader>
        {!data ? <p className="text-sm text-text-3">Chargement…</p> : (
          <div className="space-y-4">
            <div className="grid gap-2 sm:grid-cols-2">
              {data.items.filter(i => i.type !== 'document' || i.quiz_count > 0).map(i => (
                <button key={i.id} type="button" onClick={() => setSel(i.id)} data-testid={`suivi-item-${i.id}`}
                  className={`text-left rounded-ctrl border px-3 py-2 transition-colors ${sel === i.id ? 'border-orange bg-orange/5' : 'border-border hover:bg-surface-2'}`}>
                  <span className="block text-[11px] uppercase tracking-wide text-label">{i.famille} · {TYPE_LABEL[i.type]}</span>
                  <span className="block text-sm font-semibold text-ink truncate">{i.titre}</span>
                  <span className="block text-xs text-text-3">{i.nb_viewers} personne(s) · {i.nb_completed} terminé(s){i.quiz_count > 0 ? ` · quiz validé : ${i.viewers.filter(v => v.quiz?.passed).length}` : ''}</span>
                </button>
              ))}
            </div>
            {item && (
              <div className="rounded-card border border-border overflow-hidden">
                <table className="w-full text-sm" data-testid="suivi-table">
                  <thead className="bg-surface-2 text-[11px] uppercase tracking-wide text-label">
                    <tr><th className="text-left px-3 py-2">Utilisateur</th><th className="text-left px-3 py-2 w-56">Progression</th><th className="text-left px-3 py-2">Position max.</th><th className="text-left px-3 py-2">Ouvertures</th>{item.quiz_count > 0 && <th className="text-left px-3 py-2">Quiz</th>}<th className="text-left px-3 py-2">Dernière fois</th></tr>
                  </thead>
                  <tbody>
                    {item.viewers.map(v => (
                      <tr key={v.user_id} className="border-t border-border/60" data-testid={`suivi-row-${v.user_id}`}>
                        <td className="px-3 py-2 text-ink">{v.user_name}</td>
                        <td className="px-3 py-2"><ProgressBar value={v.percent} /></td>
                        <td className="px-3 py-2 text-text-3 tabular-nums">{fmtPos(v, item.type)}</td>
                        <td className="px-3 py-2 text-text-3 tabular-nums">{v.views || 0}</td>
                        {item.quiz_count > 0 && (
                          <td className="px-3 py-2" data-testid={`suivi-quiz-${v.user_id}`}>
                            {v.quiz ? <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${v.quiz.passed ? 'bg-emerald-100 text-emerald-700' : 'bg-orange/10 text-orange-700'}`}>{v.quiz.passed ? 'Validé' : 'Non validé'} · {v.quiz.best_score} % ({v.quiz.attempts}×)</span> : <span className="text-xs text-text-3">—</span>}
                          </td>
                        )}
                        <td className="px-3 py-2 text-text-3">{fmt(v.last_at || v.quiz?.last_at)}</td>
                      </tr>
                    ))}
                    {!item.viewers.length && <tr><td colSpan={6} className="px-3 py-4 text-center text-text-3">Personne n'a encore ouvert cet élément.</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
            {item && notSeen.length > 0 && (
              <div data-testid="suivi-not-seen">
                <p className="text-xs font-semibold uppercase tracking-wide text-label mb-1.5">Pas encore vu ({notSeen.length})</p>
                <p className="text-[13px] text-text-3 leading-relaxed">{notSeen.map(u => u.name || u.email).join(' · ')}</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
